import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScorekeeperSectionData } from '@/types/unifiedGameForm';
import { GameWithDetails } from '@/types/gameSystem';
import { Trophy, Loader2, Save, UserPlus, CheckCircle, Clock, XCircle, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ScorekeeperSectionProps {
  gameData: GameWithDetails;
  canEdit: boolean;
  canApprove: boolean;
  onSave: (data: ScorekeeperSectionData) => void;
  saving: boolean;
}

interface ScorekeeperApplication {
  id: string;
  scorekeeper_id: string;
  status: string;
  applied_at: string;
  scorekeeper?: {
    username?: string;
    first_name?: string;
    last_name?: string;
  };
}

interface AttendingPlayer {
  player_id: string;
  player_type: string;
  team_side: string;
  player?: {
    username?: string;
    first_name?: string;
    last_name?: string;
  };
  stats?: {
    points?: number;
    assists?: number;
    goals?: number;
    saves?: number;
    fouls?: number;
  };
}

export const ScorekeeperSection: React.FC<ScorekeeperSectionProps> = ({
  gameData,
  canEdit,
  canApprove,
  onSave,
  saving
}) => {
  const [formData, setFormData] = useState<ScorekeeperSectionData>({
    scoring: {
      homeScore: 0,
      awayScore: 0,
      gameTime: '00:00',
      period: '1st Half'
    },
    stats: {
      playerStats: {},
      teamStats: {}
    },
    events: {
      penalties: [],
      substitutions: [],
      goals: [],
      fouls: []
    }
  });

  const [currentUser, setCurrentUser] = useState<any>(null);
  const [userApplication, setUserApplication] = useState<ScorekeeperApplication | null>(null);
  const [allApplications, setAllApplications] = useState<ScorekeeperApplication[]>([]);
  const [applying, setApplying] = useState(false);
  const [attendingPlayers, setAttendingPlayers] = useState<AttendingPlayer[]>([]);
  const [playerStats, setPlayerStats] = useState<Record<string, any>>({});
  const [savingStats, setSavingStats] = useState(false);

  const gameInProgress = gameData.game_status === 'in_progress';
  const gameCompleted = gameData.game_status === 'completed';

  useEffect(() => {
    loadCurrentUser();
    loadScorekeeperApplications();
    if (gameInProgress || gameCompleted) {
      loadAttendingPlayers();
    }
  }, [gameData.id]);

  const loadCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);
  };

  const loadScorekeeperApplications = async () => {
    try {
      const { data: applications, error } = await supabase
        .from('scorekeeper_applications')
        .select('*')
        .eq('game_id', gameData.id)
        .order('applied_at', { ascending: false });

      if (error) throw error;

      // Get scorekeeper profiles separately
      if (applications && applications.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, first_name, last_name')
          .in('id', applications.map(a => a.scorekeeper_id));

        const appsWithProfiles = applications.map(app => ({
          ...app,
          scorekeeper: profiles?.find(p => p.id === app.scorekeeper_id)
        }));

        setAllApplications(appsWithProfiles as any);

        // Find current user's application
        if (currentUser) {
          const myApp = appsWithProfiles.find(app => app.scorekeeper_id === currentUser.id);
          setUserApplication(myApp as any || null);
        }
      } else {
        setAllApplications([]);
      }
    } catch (error) {
      console.error('Error loading scorekeeper applications:', error);
    }
  };

  const loadAttendingPlayers = async () => {
    try {
      const { data: players, error } = await supabase
        .from('player_attendance_status')
        .select(`
          player_id,
          player_type,
          team_side
        `)
        .eq('game_id', gameData.id)
        .eq('attendance_status', 'attending');

      if (error) throw error;

      // Get player profiles separately
      if (players && players.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, first_name, last_name')
          .in('id', players.map(p => p.player_id));

        const playersWithProfiles = players.map(p => ({
          ...p,
          player: profiles?.find(prof => prof.id === p.player_id)
        }));

        setAttendingPlayers(playersWithProfiles as any);
      }

      // Load existing stats for these players
      const { data: existingStats } = await supabase
        .from('player_stats')
        .select('*')
        .eq('game_id', gameData.id as any)
        .in('player_id', (players || []).map(p => p.player_id));

      // Initialize player stats
      const statsMap: Record<string, any> = {};
      (existingStats || []).forEach(stat => {
        if (!statsMap[stat.player_id]) {
          statsMap[stat.player_id] = {};
        }
        statsMap[stat.player_id][stat.stat_type] = stat.stat_value;
      });

      setPlayerStats(statsMap);
    } catch (error) {
      console.error('Error loading attending players:', error);
    }
  };

  const handleApplyAsScorekeeper = async () => {
    if (!currentUser) {
      toast.error('You must be logged in to apply');
      return;
    }

    setApplying(true);
    try {
      const { error } = await supabase
        .from('scorekeeper_applications')
        .insert({
          game_id: gameData.id as any,
          scorekeeper_id: currentUser.id,
          status: 'pending',
          application_notes: 'Application to be scorekeeper for this game'
        });

      if (error) throw error;

      toast.success('Application submitted successfully');
      await loadScorekeeperApplications();
    } catch (error: any) {
      console.error('Error applying as scorekeeper:', error);
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const handlePlayerStatChange = (playerId: string, statType: string, value: number) => {
    setPlayerStats(prev => ({
      ...prev,
      [playerId]: {
        ...(prev[playerId] || {}),
        [statType]: value
      }
    }));
  };

  const handleSavePlayerStats = async () => {
    setSavingStats(true);
    try {
      // Prepare stats for insertion/update
      const statsToSave: any[] = [];
      
      Object.entries(playerStats).forEach(([playerId, stats]) => {
        Object.entries(stats).forEach(([statType, statValue]) => {
          if (typeof statValue === 'number' && statValue > 0) {
          statsToSave.push({
              player_id: playerId,
              game_id: gameData.id as any,
              sport_id: gameData.sport_id || 1,
              stat_type: statType,
              stat_value: statValue,
              stat_submitted_by: currentUser?.id,
              is_confirmed: false, // Awaiting approval
              season_year: new Date().getFullYear()
            });
          }
        });
      });

      if (statsToSave.length === 0) {
        toast.error('No stats to save');
        return;
      }

      // Delete existing stats for this game (we'll re-insert)
      await supabase
        .from('player_stats')
        .delete()
        .eq('game_id', gameData.id as any);

      // Insert new stats
      const { error } = await supabase
        .from('player_stats')
        .insert(statsToSave);

      if (error) throw error;

      toast.success('Player stats saved successfully');
      await loadAttendingPlayers(); // Reload to show updated stats
    } catch (error: any) {
      console.error('Error saving player stats:', error);
      toast.error(error.message || 'Failed to save player stats');
    } finally {
      setSavingStats(false);
    }
  };

  const handleInputChange = (section: keyof ScorekeeperSectionData, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const getApplicationStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const getPlayerName = (player: AttendingPlayer) => {
    if (player.player?.first_name && player.player?.last_name) {
      return `${player.player.first_name} ${player.player.last_name}`;
    }
    return player.player?.username || 'Unknown Player';
  };

  return (
    <div className="space-y-6">
      {/* Scorekeeper Sign-Up Section */}
      {!gameInProgress && !gameCompleted && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Scorekeeper Application
            </CardTitle>
            <CardDescription>
              Apply to be the scorekeeper for this game
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!userApplication ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  You haven't applied to be a scorekeeper for this game yet.
                </p>
                <Button onClick={handleApplyAsScorekeeper} disabled={applying}>
                  {applying ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Apply as Scorekeeper
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Your Application Status:</span>
                  {getApplicationStatusBadge(userApplication.status)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Applied on {new Date(userApplication.applied_at).toLocaleString()}
                </p>
              </div>
            )}

            {/* Show all applications */}
            {allApplications.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-semibold mb-3">All Scorekeeper Applications ({allApplications.length})</h4>
                <div className="space-y-2">
                  {allApplications.map(app => (
                    <div key={app.id} className="flex items-center justify-between p-3 bg-muted/40 rounded-lg">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {app.scorekeeper?.first_name && app.scorekeeper?.last_name
                            ? `${app.scorekeeper.first_name} ${app.scorekeeper.last_name}`
                            : app.scorekeeper?.username || 'Unknown'}
                        </span>
                      </div>
                      {getApplicationStatusBadge(app.status)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Score Tracking (Main Scorekeeper Interface) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Scorekeeper Interface
            {!canEdit && <Badge variant="outline">View Only</Badge>}
          </CardTitle>
          <CardDescription>
            Record scores, track statistics, and manage game events
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Score Tracking */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Current Score</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <Label htmlFor="homeScore">Home Team</Label>
                <div className="text-3xl font-bold mt-2">
                  {formData.scoring.homeScore}
                </div>
                <Input
                  id="homeScore"
                  type="number"
                  value={formData.scoring.homeScore}
                  onChange={(e) => handleInputChange('scoring', 'homeScore', parseInt(e.target.value) || 0)}
                  disabled={!canEdit}
                  className="text-center mt-2"
                />
                <div className="text-sm text-muted-foreground mt-1">
                  {gameData.home_team_label}
                </div>
              </div>
              <div className="text-center">
                <Label htmlFor="awayScore">Away Team</Label>
                <div className="text-3xl font-bold mt-2">
                  {formData.scoring.awayScore}
                </div>
                <Input
                  id="awayScore"
                  type="number"
                  value={formData.scoring.awayScore}
                  onChange={(e) => handleInputChange('scoring', 'awayScore', parseInt(e.target.value) || 0)}
                  disabled={!canEdit}
                  className="text-center mt-2"
                />
                <div className="text-sm text-muted-foreground mt-1">
                  {gameData.away_team_label}
                </div>
              </div>
            </div>
          </div>

          {/* Game Time */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Game Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="gameTime">Game Time</Label>
                <Input
                  id="gameTime"
                  value={formData.scoring.gameTime}
                  onChange={(e) => handleInputChange('scoring', 'gameTime', e.target.value)}
                  disabled={!canEdit}
                  placeholder="00:00"
                />
              </div>
              <div>
                <Label htmlFor="period">Period</Label>
                <Input
                  id="period"
                  value={formData.scoring.period}
                  onChange={(e) => handleInputChange('scoring', 'period', e.target.value)}
                  disabled={!canEdit}
                  placeholder="1st Half"
                />
              </div>
            </div>
          </div>

          {/* Game Events */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Game Events</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{formData.events.goals.length}</div>
                <div className="text-sm text-muted-foreground">Goals</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{formData.events.fouls.length}</div>
                <div className="text-sm text-muted-foreground">Fouls</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{formData.events.penalties.length}</div>
                <div className="text-sm text-muted-foreground">Penalties</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{formData.events.substitutions.length}</div>
                <div className="text-sm text-muted-foreground">Substitutions</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          {canEdit && (
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Game Data
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Player Stats Input (Only when game is in progress or completed) */}
      {(gameInProgress || gameCompleted) && canEdit && attendingPlayers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" />
              Player Statistics
            </CardTitle>
            <CardDescription>
              Record individual player stats for this game
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              {attendingPlayers.map(player => (
                <div key={player.player_id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{getPlayerName(player)}</h4>
                      <p className="text-xs text-muted-foreground">
                        {player.team_side === 'home' ? gameData.home_team_label : gameData.away_team_label}
                      </p>
                    </div>
                    <Badge variant={player.team_side === 'home' ? 'default' : 'secondary'}>
                      {player.team_side.toUpperCase()}
                    </Badge>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <Label className="text-xs">Points</Label>
                      <Input
                        type="number"
                        min="0"
                        value={playerStats[player.player_id]?.points || 0}
                        onChange={(e) => handlePlayerStatChange(player.player_id, 'points', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Goals</Label>
                      <Input
                        type="number"
                        min="0"
                        value={playerStats[player.player_id]?.goals || 0}
                        onChange={(e) => handlePlayerStatChange(player.player_id, 'goals', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Assists</Label>
                      <Input
                        type="number"
                        min="0"
                        value={playerStats[player.player_id]?.assists || 0}
                        onChange={(e) => handlePlayerStatChange(player.player_id, 'assists', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Saves</Label>
                      <Input
                        type="number"
                        min="0"
                        value={playerStats[player.player_id]?.saves || 0}
                        onChange={(e) => handlePlayerStatChange(player.player_id, 'saves', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Fouls</Label>
                      <Input
                        type="number"
                        min="0"
                        value={playerStats[player.player_id]?.fouls || 0}
                        onChange={(e) => handlePlayerStatChange(player.player_id, 'fouls', parseInt(e.target.value) || 0)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={handleSavePlayerStats} disabled={savingStats}>
                {savingStats ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving Stats...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save All Player Stats
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Note: Stats will be saved as unconfirmed and require approval from coaches or team owners.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
